import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import {
  anzahlMerkmale,
  bestimme,
  fragen,
  mitAntwort,
  nichtWeiterTeilbar,
  UNBEKANNT,
  LEER,
  type Frage,
  type Merkmale,
} from '@/core/bestimmen'
import { cardFace } from '@/core/cards'
import { lightStates } from '@/core/data'
import { ruleDifferences } from '@/core/sources'
import type { LightColour } from '@/core/types'
import { useSettings } from './settings'
import { BereichWahl } from './BereichWahl'
import { MitBegriffen } from './MitBegriffen'
import { Viewer, type Subject } from './Viewer'

export interface BestimmenProps {
  /** Der Modus der Szene, wie überall in der App. */
  mode: 'night' | 'day' | 'red'
  /** Aus der Adresse: ein Bild, das gleich offen stehen soll. */
  start?: string
}

/** Ein Wert als Zeichenkette, für Marken und Schlüssel: die Folge mit Strichen, sonst roh. */
function wertSchluessel(wert: Frage['werte'][number]): string {
  return Array.isArray(wert) ? wert.join('-') : String(wert)
}

/**
 * Bestimmen statt suchen.
 *
 * Der Bildschirm, für den diese App gebaut ist: jemand steht an Deck, sieht Lichter und
 * weiß nicht, was da fährt. Er kann nicht nach "Schleppverband" suchen, denn das Wort ist
 * die Antwort. Also tippt er an, was er sieht, und die Liste schrumpft.
 *
 * Die Reihenfolge auf dem Bildschirm ist die Reihenfolge der Handlung: Merkmale, dann
 * Treffer, dann das Bild des gewählten Treffers. Nichts springt, nichts steht oberhalb
 * dessen, was man gerade tut. Auf dem Telefon war das vorher umgekehrt, und ein Tipp auf
 * einen Treffer wirkte ins Leere, weil die Szene außerhalb des Bildschirms stand.
 */
export function Bestimmen({ mode, start }: BestimmenProps) {
  const { t, pick, lang, settings } = useSettings()
  const [merkmale, setMerkmale] = useState<Merkmale>(LEER)
  const [suche, setSuche] = useState('')
  const [gewaehlt, setGewaehlt] = useState<string | undefined>(start)
  const bereich = settings.bereich

  /**
   * Merkmale zuerst, Text daneben. Wer den Namen kennt, tippt ihn; wer ihn nicht kennt,
   * hat dafür die Merkmale. Beides greift in dieselbe Liste, und beides schrumpft sie.
   */
  const treffer = useMemo(() => {
    const gefunden = bestimme(merkmale, bereich)
    const wort = suche.trim().toLowerCase()
    if (!wort) return gefunden
    return gefunden.filter(
      (x) =>
        x.titel.de.toLowerCase().includes(wort) ||
        x.titel.en.toLowerCase().includes(wort) ||
        x.fundstelle.toLowerCase().includes(wort),
    )
  }, [merkmale, bereich, suche])
  const gesetzt = anzahlMerkmale(merkmale)

  const schalte = (aenderung: Partial<Merkmale>): void => {
    setGewaehlt(undefined)
    setMerkmale((alt) => ({ ...alt, ...aenderung }))
  }

  /**
   * Eine Antwort setzen. Was sie am Merkmalssatz ändert, steht im Kern und nicht hier:
   * dieselbe Rechnung entscheidet, welche Antworten überhaupt angeboten werden.
   */
  const setzeAntwort = (art: Frage['art'], wert: Frage['werte'][number]): void => {
    setGewaehlt(undefined)
    setMerkmale((alt) => mitAntwort(alt, art, wert))
  }

  /**
   * Ein Tipp auf einen Treffer muss sein Bild zeigen, und zwar ohne dass jemand von Hand
   * scrollt. Zwei Schwarmtests haben genau hier dasselbe gemeldet: "der Tipp wirkte ins
   * Leere", weil die Szene eine Bildschirmhöhe tiefer stand. Wer weniger Bewegung will,
   * bekommt den Sprung ohne Animation.
   */
  const detailRef = useRef<HTMLElement | null>(null)
  useEffect(() => {
    if (!gewaehlt || !detailRef.current) return
    const ruhig = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false
    detailRef.current.scrollIntoView({ behavior: ruhig ? 'auto' : 'smooth', block: 'start' })
  }, [gewaehlt])

  /**
   * Was ein Tipp auf einen Wert am Merkmalssatz ändert.
   *
   * Die Null ist eine eigene Antwort und keine Abwesenheit: "keine Topplichter" schließt
   * jedes Bild mit Topplicht aus, "nicht beantwortet" schließt nichts aus. Deshalb steht
   * hier überall die Zahl und nicht `undefined`.
   */
  const beschrifte = (art: Frage['art'], wert: Frage['werte'][number]): ReactNode => {
    if (art === 'farben') {
      const farbe = wert as LightColour
      return (
        <>
          <span className={`licht licht--${farbe}`} aria-hidden="true" />
          {t(`farbe.${farbe}`)}
        </>
      )
    }
    if (wert === UNBEKANNT) {
      return t(art === 'folge' ? 'bestimmen.uebereinander' : 'bestimmen.tagzeichen')
    }
    if (art === 'folge') {
      const folge = wert as LightColour[]
      return folge.length === 0 ? t('bestimmen.keins') : folgeZeichen(folge)
    }
    if (art === 'seitenlicht' || art === 'blinkt') {
      return t(`bestimmen.${art}.${wert === 1 ? 'ja' : 'nein'}` as never)
    }
    if (art === 'tagform') return t(`shape.${String(wert)}` as never)
    const zahl = wert as number
    if (zahl === 0) return t('bestimmen.keins')
    if (zahl === 3) return t('bestimmen.weisse.drei')
    return String(zahl)
  }

  /** Die Folge als Bild: die Lichter stehen übereinander, wie am Mast. */
  const folgeZeichen = (folge: LightColour[]): ReactNode => (
    <>
      <span className="folge" aria-hidden="true">
        {folge.map((farbe, i) => (
          <span key={i} className={`licht licht--${farbe}`} />
        ))}
      </span>
      <span className="folge__wort">
        {folge.map((farbe) => t(`farbe.${farbe}`)).join(` ${t('bestimmen.folge.ueber')} `)}
      </span>
    </>
  )

  /** Die schon gegebenen Antworten, jede mit dem Weg zurück. */
  const chips: { id: string; text: ReactNode; weg: Partial<Merkmale> }[] = [
    ...merkmale.farben.map((farbe) => ({
      id: `farbe-${farbe}`,
      text: beschrifte('farben', farbe),
      weg: { farben: merkmale.farben.filter((f) => f !== farbe) },
    })),
    ...(merkmale.folge
      ? [
          {
            id: 'folge',
            text: beschrifte('folge', merkmale.folge),
            weg: { folge: undefined },
          },
        ]
      : []),
    ...(merkmale.uebereinander
      ? [
          {
            id: 'uebereinander',
            text: t('bestimmen.uebereinander'),
            weg: { uebereinander: undefined },
          },
        ]
      : []),
    ...(merkmale.tagzeichen
      ? [
          {
            id: 'tagzeichen',
            text: t('bestimmen.tagzeichen'),
            weg: { tagzeichen: undefined },
          },
        ]
      : []),
    ...(['weisse', 'topp', 'rundum'] as const)
      .filter((art) => merkmale[art] !== undefined)
      .map((art) => ({
        id: art,
        text: (
          <>
            {t(`bestimmen.${art}` as never)}: {beschrifte(art, merkmale[art] as number)}
          </>
        ),
        weg: { [art]: undefined } as Partial<Merkmale>,
      })),
    ...(['seitenlicht', 'blinkt'] as const)
      .filter((art) => merkmale[art] !== undefined)
      .map((art) => ({
        id: art,
        text: beschrifte(art, merkmale[art] === true ? 1 : 0),
        weg: { [art]: undefined } as Partial<Merkmale>,
      })),
    ...(merkmale.tagform
      ? [{ id: 'tagform', text: beschrifte('tagform', merkmale.tagform), weg: { tagform: undefined } }]
      : []),
  ]

  const liste = useMemo(() => fragen(merkmale, bereich, mode === 'day'), [merkmale, bereich, mode])
  const schluss = useMemo(
    () => nichtWeiterTeilbar(merkmale, bereich, mode === 'day'),
    [merkmale, bereich, mode],
  )

  const gewaehltesBild = gewaehlt ? treffer.find((x) => x.id === gewaehlt) : undefined
  const karte = gewaehltesBild
    ? cardFace(`${bereich === 'binnen' ? 'binnen' : 'state'}:${gewaehltesBild.id}`, lang)
    : undefined
  // Nur der Seebestand kennt die Unterscheidung international gegen US-Binnen.
  const seeBild = gewaehltesBild && bereich === 'see'
    ? lightStates.find((x) => x.id === gewaehltesBild.id)
    : undefined
  const subject: Subject | undefined = gewaehltesBild
    ? { kind: 'vessel', id: gewaehltesBild.id }
    : undefined

  return (
    <section className="bestimmen" data-testid="bestimmen" data-bereich={bereich}>
      {/* Wo man fährt, entscheidet, welche Ordnung gilt: auf dem Rhein führt dasselbe
          Fahrzeug ein anderes Lichterbild als auf der Ostsee. */}
      <BereichWahl
        onWechsel={() => {
          setMerkmale(LEER)
          setSuche('')
          setGewaehlt(undefined)
        }}
      />
      {/**
        * Die Fragen stehen in der Reihenfolge, in der sie etwas bringen.
        *
        * Ein Bestimmungsschlüssel ist nur so gut wie seine nächste Frage. Welche das ist,
        * hängt an der Treffermenge, die gerade übrig ist, und ändert sich mit jeder
        * Antwort: am Anfang teilen die Rundumlichter am schärfsten, nach "rot über rot"
        * ist diese Frage erledigt und verschwindet. Die Oberfläche rechnet das aus,
        * statt den Nutzer raten zu lassen, was ihn weiterbringt.
        */}
      <div className="bestimmen__merkmale">
        {gesetzt > 0 && (
          <div className="bestimmen__zeile">
            <span className="bestimmen__was">{t('bestimmen.gesetzt')}</span>
            <span className="modes">
              {chips.map((chip) => (
                <button
                  key={chip.id}
                  type="button"
                  className="modes__item modes__item--on bestimmen__chip"
                  data-chip={chip.id}
                  data-testid={`chip-${chip.id}`}
                  onClick={() => schalte(chip.weg)}
                >
                  {chip.text}
                  <span className="bestimmen__weg" aria-hidden="true">
                    ×
                  </span>
                </button>
              ))}
            </span>
          </div>
        )}

        {liste.map((frage, i) => (
          <div
            className={`bestimmen__zeile${i === 0 ? ' bestimmen__zeile--erste' : ''}`}
            key={frage.art}
            data-frage={frage.art}
            data-testid={`frage-${frage.art}`}
          >
            <span className="bestimmen__was">{t(`bestimmen.${frage.art}` as never)}</span>
            {/* Viele Antworten stapeln sich sonst untereinander und füllen auf dem
                Telefon den ganzen Bildschirm: zehn Farbfolgen sind zehn Zeilen. */}
            <span className={`modes${frage.werte.length > 6 ? ' modes--raster' : ''}`}>
              {frage.werte.map((wert) => {
                const schluessel = wertSchluessel(wert)
                return (
                  <button
                    key={schluessel}
                    type="button"
                    className="modes__item"
                    data-wert={schluessel}
                    data-testid={`wahl-${frage.art}-${schluessel}`}
                    onClick={() => setzeAntwort(frage.art, wert)}
                  >
                    {beschrifte(frage.art, wert)}
                  </button>
                )
              })}
            </span>
          </div>
        ))}

        {/**
          * Die zweite Achse: nicht die Merkmale entscheiden über die Schärfe, sondern wie
          * sicher der Beobachter sich ist. Wer das Fahrzeug frei und ganz vor sich hat,
          * kann sagen "mehr ist da nicht", und aus 37 Bildern wird im Mittel eines. Wer
          * das aus einer schrägen Peilung behauptet, filtert sich seinen Treffer weg.
          */}
        <div className="bestimmen__zeile bestimmen__sicht">
          <button
            type="button"
            className={`modes__item${merkmale.sichtFrei ? ' modes__item--on' : ''}`}
            data-testid="sicht-frei"
            aria-pressed={merkmale.sichtFrei === true}
            onClick={() => schalte({ sichtFrei: merkmale.sichtFrei ? undefined : true })}
          >
            {t('bestimmen.sichtfrei')}
          </button>
          <p className="bestimmen__hilfe">{t('bestimmen.sichtfrei.hilfe')}</p>
        </div>
      </div>

      <div className="catalogue__head">
        <input
          type="search"
          className="catalogue__search"
          data-testid="bestimmen-suche"
          placeholder={t(bereich === 'binnen' ? 'bestimmen.suche.binnen' : 'bestimmen.suche')}
          value={suche}
          onChange={(e) => {
            setSuche(e.target.value)
            setGewaehlt(undefined)
          }}
        />
      </div>

      <div className="bestimmen__stand">
        <span data-testid="bestimmen-zahl">
          {t('bestimmen.treffer', { treffer: treffer.length, alle: bestimme(LEER, bereich).length })}
        </span>
        {(gesetzt > 0 || suche) && (
          <button
            type="button"
            className="toggle"
            data-testid="bestimmen-zuruecksetzen"
            onClick={() => {
              setMerkmale(LEER)
              setSuche('')
              setGewaehlt(undefined)
            }}
          >
            {t('bestimmen.zuruecksetzen')}
          </button>
        )}
      </div>

      {treffer.length === 0 && (
        <p className="catalogue__empty" data-testid="bestimmen-leer">
          {t('bestimmen.leer')}
        </p>
      )}

      {/**
        * Manche Fahrzeuge führen dieselben Lichter, und dann ist nicht der Schlüssel
        * schlecht, sondern die Ordnung eindeutig: ein Schubverband in fester Verbindung
        * gilt nach Regel 24(b) als Maschinenfahrzeug. Die App sagt das, statt weiter zu
        * fragen und eine Eindeutigkeit vorzutäuschen, die es nicht gibt.
        */}
      {schluss && (
        <p className="bestimmen__schluss" data-testid="bestimmen-schluss">
          {t('bestimmen.schluss')}
        </p>
      )}

      <div className="bestimmen__treffer">
        {treffer.map((x) => (
          <button
            key={x.id}
            type="button"
            className={`states__item${x.id === gewaehlt ? ' states__item--on' : ''}`}
            data-state={x.id}
            data-testid={`treffer-${x.id}`}
            onClick={() => setGewaehlt(x.id === gewaehlt ? undefined : x.id)}
          >
            <span className="states__title">{pick(x.titel)}</span>
            <span className="lichter" aria-hidden="true">
              {x.farben.map((farbe) => (
                <span key={farbe} className={`licht licht--${farbe}`} />
              ))}
            </span>
            <span className="states__scope">
              {x.fundstelle}
              {x.geltung && (
                <>
                  {' · '}
                  <strong data-geltung={x.geltung}>
                    {t(x.geltung === 'international' ? 'scope.international' : 'scope.inland')}
                  </strong>
                </>
              )}
            </span>
          </button>
        ))}
      </div>

      {subject && karte && (
        <article className="detail" data-testid="bestimmen-detail" ref={detailRef}>
          <Viewer subject={subject} mode={mode} />
          <h2>{karte.title}</h2>
          <MitBegriffen
            text={karte.explanation}
            className="detail__text"
            lang={bereich === 'binnen' ? 'de' : undefined}
          />
          <h3 className="detail__legend">{karte.citation}</h3>
          <ul className="detail__quotes">
            {karte.quotes.map((quote, i) => (
              <li key={i} lang={bereich === 'binnen' ? 'de' : undefined}>
                {quote}
              </li>
            ))}
          </ul>
          {/**
            * Wo die US-Binnenregeln von den internationalen abweichen, sagt die App das
            * an der Stelle, an der der Regeltext steht. Die Unterscheidung ist ein
            * Merkmal dieses Bestands und keine Fußnote: ein kurzer Ton heißt
            * international "ich drehe nach Steuerbord" und inland "ich will dich an
            * Backbord passieren lassen".
            */}
          {seeBild && ruleDifferences(seeBild.rule).length > 0 && (
            <details className="detail__inland" data-testid="inland-note">
              <summary>
                {t('inland.summary', {
                  rule: seeBild.rule,
                  ids: ruleDifferences(seeBild.rule)
                    .map((teil) => (teil.id === 'intro' ? t('inland.whole') : `(${teil.id})`))
                    .join(', '),
                })}
              </summary>
              <ul>
                {ruleDifferences(seeBild.rule).map((teil) => (
                  <li key={teil.id}>
                    <strong>{teil.id === 'intro' ? 'Inland' : `(${teil.id})`}</strong>{' '}
                    {teil.inland || t('inland.none')}
                  </li>
                ))}
              </ul>
            </details>
          )}
          {/* Woher der Text kommt und was die App nicht ist. Der Satz gehört an jeden
              Regeltext, nicht an einen Abschnitt, den man erst suchen muss. */}
          <p className="detail__disclaimer">
            {bereich === 'binnen'
              ? t('detail.disclaimer.binnen')
              : t('detail.disclaimer.rules', { source: t('detail.source.navrules') })}
          </p>
        </article>
      )}
    </section>
  )
}
